import { Component } from '@theme/component';
import {
  fetchConfig,
  debounce,
  prefersReducedMotion,
  resetShimmer,
} from '@theme/utilities';
import { morphSection, sectionRenderer } from '@theme/section-renderer';
import {
  ThemeEvents,
  CartUpdateEvent,
  QuantitySelectorUpdateEvent,
  CartAddEvent,
  DiscountUpdateEvent,
} from '@theme/events';
import { cartPerformance } from '@theme/performance';
import { requestCartJson } from '@theme/prevura-cart-recovery';
import {
  getPrevuraDisplayItemCount,
  isPrevuraPackageHandle,
  normalizePrevuraPackageCart,
} from '@theme/prevura-cart-contract';

/** @typedef {import('./utilities').TextComponent} TextComponent */

// Page and drawer share the same server cart. A lost response can shift Shopify
// line numbers, so require a fresh page before any further quantity mutation.
let cartQuantityRequestInProgress = false;
let cartQuantityStateUncertain = false;

const cartAcknowledgementSelectors = [
  'input[name="attributes[Teilnahmebedingungen Präventionskurs]"]',
  'input[name="attributes[Vorzeitiger Beginn digitaler Kurszugang]"]',
];

/**
 * Preserve required acknowledgements while Shopify replaces cart sections.
 * The cart page and drawer can exist at the same time, so either checked
 * instance is treated as the current browser-session state.
 * @returns {boolean[]}
 */
function getCartAcknowledgementState() {
  return cartAcknowledgementSelectors.map((selector) => {
    const inputs = Array.from(document.querySelectorAll(selector));

    // A newly populated cart must require a deliberate confirmation. Never
    // manufacture consent merely because the preceding cart was empty.
    if (inputs.length === 0) return false;

    return inputs.some((input) => input instanceof HTMLInputElement && input.checked);
  });
}

const cartDisclosureSelectors = [
  '[data-prevura-participation-requirements]',
  '.prevura-digital-card__details',
  '.prevura-cart-guarantee',
];

/**
 * Preserve disclosure state while Shopify and GiftTrigger replace the same
 * cart sections. The page and drawer copies intentionally stay in sync.
 * @returns {boolean[]}
 */
function getCartDisclosureState() {
  return cartDisclosureSelectors.map((selector) =>
    Array.from(document.querySelectorAll(selector)).some(
      (details) => details instanceof HTMLDetailsElement && details.open
    )
  );
}

/**
 * @param {boolean[]} states
 */
function restoreCartDisclosureState(states) {
  cartDisclosureSelectors.forEach((selector, index) => {
    for (const details of document.querySelectorAll(selector)) {
      if (details instanceof HTMLDetailsElement) details.open = Boolean(states[index]);
    }
  });
}

/**
 * Shopify and the gift app may replace the same drawer several times after
 * one add-to-cart action. Read the scroll position immediately before every
 * morph so a later response cannot jump the customer back to the top.
 * @returns {{ element: HTMLElement, top: number } | null}
 */
function getCartDrawerScrollState() {
  const element = document.querySelector('.cart-drawer__dialog[open] .cart-drawer__content');
  return element instanceof HTMLElement ? { element, top: element.scrollTop } : null;
}

/** @param {{ element: HTMLElement, top: number } | null} state */
function restoreCartDrawerScrollState(state) {
  if (!state) return;
  const element = document.querySelector('.cart-drawer__dialog[open] .cart-drawer__content');
  if (!(element instanceof HTMLElement)) return;

  requestAnimationFrame(() => {
    element.scrollTop = Math.min(state.top, Math.max(0, element.scrollHeight - element.clientHeight));
  });
}

/**
 * Hydration updates the drawer contents but intentionally preserves the outer
 * dialog. Keep its visual and accessible state in sync across empty/filled
 * transitions instead of relying on a full section replacement.
 */
function syncCartDrawerState() {
  for (const dialog of document.querySelectorAll('.cart-drawer__dialog')) {
    if (!(dialog instanceof HTMLDialogElement)) continue;

    const isEmpty = !dialog.querySelector('.cart-form');
    const heading = dialog.querySelector(isEmpty ? '#cart-drawer-heading-empty' : '#cart-drawer-heading');
    const content = dialog.querySelector('.cart-drawer__content');

    dialog.classList.toggle('cart-drawer--empty', isEmpty);
    if (heading instanceof HTMLElement && heading.id) {
      dialog.setAttribute('aria-labelledby', heading.id);
    } else {
      dialog.removeAttribute('aria-labelledby');
    }

    if (content instanceof HTMLElement) {
      content.scrollTop = Math.min(content.scrollTop, Math.max(0, content.scrollHeight - content.clientHeight));
    }
  }
}

/**
 * @param {boolean[]} states
 */
function restoreCartAcknowledgementState(states) {
  cartAcknowledgementSelectors.forEach((selector, index) => {
    for (const input of document.querySelectorAll(selector)) {
      if (input instanceof HTMLInputElement) input.checked = Boolean(states[index]);
    }
  });
  updateCartAcknowledgementUi();
}

/**
 * @returns {boolean[]}
 */
function emptyCartAcknowledgementState() {
  return cartAcknowledgementSelectors.map(() => false);
}

/**
 * @param {boolean} removesPrevuraPackage
 * @param {boolean[]} states
 * @returns {boolean[]}
 */
function resolveCartAcknowledgementState(removesPrevuraPackage, states) {
  if (removesPrevuraPackage) {
    return emptyCartAcknowledgementState();
  }
  return states;
}

/**
 * Keep the drawer and cart-page copies aligned and expose one concise
 * completion state immediately before checkout.
 */
function updateCartAcknowledgementUi() {
  const states = getCartAcknowledgementState();
  const remaining = states.filter((state) => !state).length;

  for (const status of document.querySelectorAll('[data-prevura-ack-status]')) {
    status.textContent =
      remaining === 0
        ? 'Beide Bestätigungen sind vollständig'
        : remaining === 1
          ? 'Noch 1 Bestätigung vor dem Checkout erforderlich'
          : '2 Bestätigungen vor dem Checkout erforderlich';
    status.classList.toggle('is-complete', remaining === 0);
  }

  cartAcknowledgementSelectors.forEach((selector, index) => {
    for (const input of document.querySelectorAll(selector)) {
      if (!(input instanceof HTMLInputElement)) continue;
      const label = input.closest('.pv4-cart-confirm');
      label?.classList.toggle('is-complete', states[index]);
      if (states[index]) {
        input.removeAttribute('aria-invalid');
        label?.classList.remove('has-error');
        const error = label?.querySelector('[data-prevura-ack-error]');
        if (error instanceof HTMLElement) error.hidden = true;
      }
    }
  });
}

let pendingAcknowledgementFocus = null;

function handleCartAcknowledgementInvalid(event) {
  const input = event.target;
  if (
    !(input instanceof HTMLInputElement) ||
    !cartAcknowledgementSelectors.some((selector) => input.matches(selector))
  ) {
    return;
  }

  event.preventDefault();
  input.setAttribute('aria-invalid', 'true');
  const label = input.closest('.pv4-cart-confirm');
  label?.classList.add('has-error');
  const error = label?.querySelector('[data-prevura-ack-error]');
  if (error instanceof HTMLElement) error.hidden = false;

  if (pendingAcknowledgementFocus) return;
  pendingAcknowledgementFocus = input;
  requestAnimationFrame(() => {
    const target = pendingAcknowledgementFocus;
    pendingAcknowledgementFocus = null;
    const targetLabel = target?.closest('.pv4-cart-confirm');
    targetLabel?.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'center' });
    target?.focus({ preventScroll: true });
    updateCartAcknowledgementUi();
  });
}

function handleCartAcknowledgementChange(event) {
  const input = event.target;
  if (
    !(input instanceof HTMLInputElement) ||
    !cartAcknowledgementSelectors.some((selector) => input.matches(selector))
  ) {
    return;
  }

  const selector = cartAcknowledgementSelectors.find((candidate) => input.matches(candidate));
  if (!selector) return;

  for (const mirror of document.querySelectorAll(selector)) {
    if (mirror instanceof HTMLInputElement) mirror.checked = input.checked;
  }
  updateCartAcknowledgementUi();
}

/**
 * The mobile checkout dock intentionally overlays the drawer's lower edge.
 * Once the participation disclosure expands, reveal its complete copy above
 * that dock instead of leaving the final lines hidden underneath it.
 * @param {Event} event
 */
function handleCartParticipationToggle(event) {
  const details = event.target;
  if (
    !(details instanceof HTMLDetailsElement) ||
    !details.matches('[data-prevura-participation-requirements]') ||
    !details.open
  ) {
    return;
  }

  revealCartDisclosure(details);
}

/**
 * Leave the complete disclosure above the fixed checkout dock. Safari can
 * dispatch the non-bubbling `toggle` event inconsistently after a morph, so
 * the summary click path below calls the same routine as a fallback.
 * @param {HTMLDetailsElement} details
 */
function revealCartDisclosure(details) {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      if (!details.open) return;

      const drawerContent = details.closest('.cart-drawer__content');
      const checkoutDock = details.closest('.cart-drawer__dialog')?.querySelector('.pv-cart-mobile-checkout');
      if (!(drawerContent instanceof HTMLElement) || !(checkoutDock instanceof HTMLElement)) return;

      const detailsBottom = details.getBoundingClientRect().bottom;
      const visibleBottom = checkoutDock.getBoundingClientRect().top - 28;
      const hiddenAmount = detailsBottom - visibleBottom;
      if (hiddenAmount <= 0) return;

      drawerContent.scrollBy({
        top: hiddenAmount + 12,
        behavior: prefersReducedMotion() ? 'auto' : 'smooth',
      });
    });
  });
}

function handleCartDisclosureSummaryClick(event) {
  if (!(event.target instanceof Element)) return;
  const summary = event.target.closest('[data-prevura-participation-requirements] > summary');
  const details = summary?.parentElement;
  if (!(details instanceof HTMLDetailsElement)) return;
  window.setTimeout(() => revealCartDisclosure(details), 0);
}

/**
 * A custom element that displays a cart items component.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} quantitySelectors - The quantity selector elements.
 * @property {HTMLTableRowElement[]} cartItemRows - The cart item rows.
 * @property {TextComponent} cartTotal - The cart total.
 *
 * @extends {Component<Refs>}
 */
class CartItemsComponent extends Component {
  #debouncedOnChange = debounce(this.#onQuantityChange, 300).bind(this);
  #cartRenderQueue = Promise.resolve();

  /**
   * Shopify's product form and GiftTrigger can publish consecutive cart
   * snapshots for one user action. Serialize section morphs so an older,
   * slower render can never overwrite the final gift-aware cart state.
   * @param {() => Promise<void>} render
   * @param {boolean} [propagateError] - Let a mutation expose an uncertain render result.
   * @returns {Promise<void>}
   */
  #enqueueCartRender(render, propagateError = false) {
    const rendered = this.#cartRenderQueue.then(render);
    this.#cartRenderQueue = rendered.catch((error) => {
      console.error('Cart section update could not be rendered:', error);
      if (!propagateError) {
        cartQuantityStateUncertain = true;
        this.#showUncertainCartError(0);
      }
    });

    return propagateError ? rendered : this.#cartRenderQueue;
  }

  /**
   * Resolve a cart row by Shopify's original line index. The PREVURA cart may
   * present included gift lines after their paid package for a clearer
   * semantic and visual reading order, so DOM-array position is not reliable.
   * @param {number} line
   * @returns {HTMLTableRowElement | undefined}
   */
  #getCartItemRow(line) {
    return this.refs.cartItemRows?.find((row) => Number(row.dataset.cartLine) === line);
  }

  /**
   * Resolve the quantity selector that belongs to Shopify's original line.
   * @param {number} line
   * @returns {HTMLElement | undefined}
   */
  #getQuantitySelector(line) {
    return this.refs.quantitySelectors?.find(
      (selector) => Number(selector.querySelector('input[data-cart-line]')?.dataset.cartLine) === line
    );
  }

  connectedCallback() {
    super.connectedCallback();

    document.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.addEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.addEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
    this.#enforcePrevuraPackageContract();

    customElements.whenDefined('cart-quantity-selector-component').then(() => {
      if (!this.isConnected) return;
      requestAnimationFrame(() => {
        if (this.isConnected) this.#updateCartQuantitySelectorButtonStates();
      });
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    document.removeEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate);
    document.removeEventListener(ThemeEvents.discountUpdate, this.handleDiscountUpdate);
    document.removeEventListener(ThemeEvents.quantitySelectorUpdate, this.#debouncedOnChange);
  }

  async #enforcePrevuraPackageContract() {
    if (document.querySelector('cart-items-component') !== this) return;

    try {
      const result = await normalizePrevuraPackageCart({
        sectionIds: [this.sectionId],
        sectionsUrl: window.location.pathname,
      });

      if (!result.changed) return;

      document.dispatchEvent(
        new CartUpdateEvent(result.cart, 'prevura-cart-contract', {
          source: 'prevura-cart-contract',
          itemCount: getPrevuraDisplayItemCount(result.cart),
          sections: result.cart.sections,
        })
      );
    } catch (error) {
      console.error('PREVURA cart contract could not be applied:', error);
    }
  }

  /**
   * Handles QuantitySelectorUpdateEvent change event.
   * @param {QuantitySelectorUpdateEvent} event - The event.
   */
  #onQuantityChange(event) {
    if (!(event.target instanceof Node) || !this.contains(event.target)) return;

    const { quantity, cartLine: line } = event.detail;

    // Cart items require a line number
    if (!line) return;

    if (quantity === 0) {
      return this.onLineItemRemove(line);
    }

    this.updateQuantity({
      line,
      quantity,
      action: 'change',
    });
  }

  /**
   * Handles the line item removal.
   * @param {number} line - The line item index.
   */
  onLineItemRemove(line) {
    return this.updateQuantity({
      line,
      quantity: 0,
      action: 'clear',
    });
  }

  /**
   * Updates the quantity.
   * @param {Object} config - The config.
   * @param {number} config.line - The line.
   * @param {number} config.quantity - The quantity.
   * @param {string} config.action - The action.
   */
  updateQuantity(config) {
    if (cartQuantityStateUncertain) {
      this.#showUncertainCartError(config.line);
      return Promise.resolve(false);
    }
    if (cartQuantityRequestInProgress) {
      this.#resetCartLineQuantity(config.line);
      return Promise.resolve(false);
    }
    const rowToUpdate = this.#getCartItemRow(config.line);
    if (!rowToUpdate?.dataset.key) {
      cartQuantityStateUncertain = true;
      this.#showUncertainCartError(config.line);
      return Promise.resolve(false);
    }
    cartQuantityRequestInProgress = true;

    const cartPerformaceUpdateMarker = cartPerformance.createStartingMarker(`${config.action}:user-action`);
    const cartAcknowledgementState = getCartAcknowledgementState();
    const cartDisclosureState = getCartDisclosureState();

    this.#disableCartItems();

    const { line, quantity } = config;
    const { cartTotal } = this.refs;

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set([this.sectionId]);
    cartItemsComponents.forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) {
        sectionsToUpdate.add(item.dataset.sectionId);
      }
    });

    const textComponent = /** @type {TextComponent | undefined} */ (rowToUpdate?.querySelector('text-component'));
    textComponent?.shimmer();
    const removesPrevuraPackage =
      quantity === 0 && isPrevuraPackageHandle(rowToUpdate?.dataset.productHandle || '');
    const includedGiftRows = removesPrevuraPackage
      ? this.refs.cartItemRows.filter((row) => row.dataset.prevuraIncludedGift === 'true')
      : [];

    const requestRoute = removesPrevuraPackage ? Theme.routes.cart_update_url : Theme.routes.cart_change_url;

    const requestPayload =
      removesPrevuraPackage
        ? {
            updates: {
              [rowToUpdate.dataset.key]: 0,
              ...Object.fromEntries(includedGiftRows.map((row) => [row.dataset.key, 0])),
            },
            attributes: {
              'Teilnahmebedingungen Präventionskurs': '',
              'Vorzeitiger Beginn digitaler Kurszugang': '',
            },
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          }
        : {
            // A drawer may still be rendering an older snapshot. Address its
            // exact item key so a shifted line number cannot target another item.
            id: rowToUpdate.dataset.key,
            quantity,
            sections: Array.from(sectionsToUpdate).join(','),
            sections_url: window.location.pathname,
          };

    const body = JSON.stringify(requestPayload);

    cartTotal?.shimmer();

    // The shared request helper aborts after 20 seconds, including body reads.
    // Keep rows in place until a confirmed response supplies their replacement.
    return requestCartJson(requestRoute, fetchConfig('json', { body }))
      .then(async (response) => {
        const parsedResponseText = response.data;

        resetShimmer(this);

        if (parsedResponseText?.errors && (response.ok || (response.status >= 400 && response.status < 500))) {
          await this.#handleCartError(
            line,
            parsedResponseText,
            cartAcknowledgementState,
            cartDisclosureState
          );
          return false;
        }

        if (!response.ok || !Array.isArray(parsedResponseText?.items) ||
            typeof parsedResponseText.sections?.[this.sectionId] !== 'string') {
          throw new Error('Cart update response could not be confirmed');
        }

        const newSectionHTML = new DOMParser().parseFromString(
          parsedResponseText.sections[this.sectionId],
          'text/html'
        );

        // Grab the new cart item count from a hidden element
        const newCartHiddenItemCount = newSectionHTML.querySelector('[ref="cartItemCount"]')?.textContent;
        const newCartItemCount = newCartHiddenItemCount ? parseInt(newCartHiddenItemCount, 10) : 0;

        // Update data-cart-quantity for all matching variants
        this.#updateQuantitySelectors(parsedResponseText);

        await this.#enqueueCartRender(async () => {
          const drawerScrollState = getCartDrawerScrollState();
          await morphSection(this.sectionId, parsedResponseText.sections[this.sectionId], {
            mode: this.isDrawer ? 'hydration' : 'full',
          });
          restoreCartAcknowledgementState(
            resolveCartAcknowledgementState(removesPrevuraPackage, cartAcknowledgementState)
          );
          restoreCartDisclosureState(cartDisclosureState);
          restoreCartDrawerScrollState(drawerScrollState);
          syncCartDrawerState();

          this.#updateCartQuantitySelectorButtonStates();
        }, true);
        this.dispatchEvent(
          new CartUpdateEvent(parsedResponseText, this.sectionId, {
            itemCount: newCartItemCount,
            source: 'cart-items-component',
            sections: parsedResponseText.sections,
          })
        );
        return true;
      })
      .catch((error) => {
        console.error(error);
        cartQuantityStateUncertain = true;
        this.#showUncertainCartError(line);
        return false;
      })
      .finally(() => {
        resetShimmer(this);
        cartQuantityRequestInProgress = false;
        this.#enableCartItems();
        cartPerformance.measureFromMarker(cartPerformaceUpdateMarker);
      });
  }

  /**
   * Handles the discount update.
   * @param {DiscountUpdateEvent} event - The event.
   */
  handleDiscountUpdate = (event) => {
    this.#handleCartUpdate(event);
  };

  /**
   * Handles the cart error.
   * @param {number} line - The line.
   * @param {Object} parsedResponseText - The parsed response text.
   * @param {string | object} parsedResponseText.errors - The errors.
   * @param {boolean[]} acknowledgementState - Checked participation confirmations before the request.
   * @param {boolean[]} disclosureState - Open cart disclosures before the request.
   */
  #handleCartError = async (line, parsedResponseText, acknowledgementState, disclosureState) => {
    const errorMessage = this.#getCartErrorMessage(parsedResponseText);
    const cartItemRow = this.#getCartItemRow(line);

    // Standard quantity errors can stay in place. A removal, however, may
    // already have optimistically removed its row or swapped in the empty-cart
    // template. In that case the old refs no longer exist and the only safe
    // rollback is to hydrate the section from Shopify's current server state.
    if (cartItemRow && !cartItemRow.classList.contains('removing')) {
      this.#showCartLineError(line, errorMessage);
      return;
    }

    await this.#enqueueCartRender(async () => {
      const drawerScrollState = getCartDrawerScrollState();
      await sectionRenderer.renderSection(this.sectionId, { cache: false });
      restoreCartAcknowledgementState(acknowledgementState);
      restoreCartDisclosureState(disclosureState);
      restoreCartDrawerScrollState(drawerScrollState);
      syncCartDrawerState();
      this.#showCartLineError(line, errorMessage);
    });
  };

  /**
   * Restore the visible quantity and expose the server message when the line
   * still exists. Package rows intentionally have no quantity selector, so
   * every target is optional by design.
   * @param {number} line
   * @param {string} errorMessage
   * @returns {boolean}
   */
  #showCartLineError(line, errorMessage) {
    this.#resetCartLineQuantity(line);

    const cartItemError = this.refs[`cartItemError-${line}`];
    const cartItemErrorContainer = this.refs[`cartItemErrorContainer-${line}`];

    if (!(cartItemError instanceof HTMLElement) || !(cartItemErrorContainer instanceof HTMLElement)) {
      return false;
    }

    cartItemError.textContent = errorMessage;
    cartItemErrorContainer.classList.remove('hidden');
    return true;
  }

  /** @param {number} line */
  #resetCartLineQuantity(line) {
    const quantitySelector = this.#getQuantitySelector(line);
    const quantityInput = quantitySelector?.querySelector('input');

    if (quantityInput instanceof HTMLInputElement) {
      quantityInput.value = quantityInput.defaultValue;
    }

    quantitySelector?.updateButtonStates?.();
  }

  /** A response may be lost after Shopify has applied the update. Never retry it automatically. */
  #showUncertainCartError(line) {
    const message = Theme.translations.cartUpdateUncertain ||
      'Die Änderung konnte nicht bestätigt werden. Bitte lade den Warenkorb neu und prüfe den Inhalt, bevor du fortfährst.';
    const hasLineError = this.#showCartLineError(line, message);
    let error = hasLineError ? this.refs[`cartItemError-${line}`] :
      this.querySelector('[data-prevura-cart-update-error]');
    if (!error) {
      error = document.createElement('div');
      error.className = 'cart-item__error pv-feedback pv-feedback--error';
      error.setAttribute('data-prevura-cart-update-error', '');
      error.setAttribute('role', 'alert');
      this.append(error);
    }
    error.textContent = message;
    const link = document.createElement('a');
    link.href = Theme.routes.cart_url;
    link.textContent = Theme.translations.cartReload || 'Warenkorb neu laden';
    error.append(' ', link);
    this.#updateCartQuantitySelectorButtonStates();
  }

  /**
   * Shopify can return cart errors as a string, an array or a keyed object.
   * Convert every supported shape into one calm customer-facing message.
   * @param {Object} parsedResponseText
   * @returns {string}
   */
  #getCartErrorMessage(parsedResponseText) {
    const error = parsedResponseText.errors ?? parsedResponseText.description ?? parsedResponseText.message;

    if (typeof error === 'string' && error.trim()) return error;
    if (Array.isArray(error)) {
      const values = error.filter((value) => typeof value === 'string' && value.trim());
      if (values.length) return values.join(' ');
    }
    if (error && typeof error === 'object') {
      const values = Object.values(error).flat().filter((value) => typeof value === 'string' && value.trim());
      if (values.length) return values.join(' ');
    }

    return 'Der Warenkorb konnte nicht aktualisiert werden. Bitte versuche es erneut.';
  }

  /**
   * Handles the cart update.
   *
   * @param {DiscountUpdateEvent | CartUpdateEvent | CartAddEvent} event
   */
  #handleCartUpdate = (event) => {
    const cartAcknowledgementState = getCartAcknowledgementState();
    const cartDisclosureState = getCartDisclosureState();

    if (event instanceof DiscountUpdateEvent) {
      return this.#enqueueCartRender(async () => {
        const drawerScrollState = getCartDrawerScrollState();
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
        restoreCartAcknowledgementState(cartAcknowledgementState);
        restoreCartDisclosureState(cartDisclosureState);
        restoreCartDrawerScrollState(drawerScrollState);
        syncCartDrawerState();
      });
    }
    if (event.target === this) return;

    const cartItemsHtml = event.detail.data.sections?.[this.sectionId];
    return this.#enqueueCartRender(async () => {
      const drawerScrollState = getCartDrawerScrollState();
      if (cartItemsHtml) {
        await morphSection(this.sectionId, cartItemsHtml);
      } else {
        await sectionRenderer.renderSection(this.sectionId, { cache: false });
      }

      restoreCartAcknowledgementState(cartAcknowledgementState);
      restoreCartDisclosureState(cartDisclosureState);
      restoreCartDrawerScrollState(drawerScrollState);
      syncCartDrawerState();

      // Update button states for all cart quantity selectors after morph
      this.#updateCartQuantitySelectorButtonStates();
    });
  };

  /**
   * Disables the cart items.
   */
  #disableCartItems() {
    this.classList.add('cart-items-disabled');
    this.setAttribute('aria-busy', 'true');
  }

  /**
   * Enables the cart items.
   */
  #enableCartItems() {
    this.classList.remove('cart-items-disabled');
    this.removeAttribute('aria-busy');
  }

  /**
   * Updates quantity selectors for all matching variants in the cart.
   * @param {Object} updatedCart - The updated cart object.
   * @param {Array<{variant_id: number, quantity: number}>} [updatedCart.items] - The cart items.
   */
  #updateQuantitySelectors(updatedCart) {
    if (!updatedCart.items) return;

    for (const item of updatedCart.items) {
      const variantId = item.variant_id.toString();
      const selectors = document.querySelectorAll(`cart-quantity-selector-component[data-variant-id="${variantId}"]`);

      for (const selector of selectors) {
        const input = selector.querySelector('input[data-cart-quantity]');
        if (!input) continue;

        input.setAttribute('data-cart-quantity', item.quantity.toString());

        // Update the quantity selector's internal state
        if ('updateCartQuantity' in selector && typeof selector.updateCartQuantity === 'function') {
          selector.updateCartQuantity();
        }
      }
    }
  }

  /**
   * Updates button states for all cart quantity selector components.
   */
  #updateCartQuantitySelectorButtonStates() {
    for (const selector of document.querySelectorAll('cart-quantity-selector-component')) {
      /** @type {any} */ (selector).updateButtonStates?.();
    }
  }

  /**
   * Gets the section id.
   * @returns {string} The section id.
   */
  get sectionId() {
    const { sectionId } = this.dataset;

    if (!sectionId) throw new Error('Section id missing');

    return sectionId;
  }

  /**
   * @returns {boolean} Whether the component is a drawer.
   */
  get isDrawer() {
    return this.dataset.drawer !== undefined;
  }
}

if (!customElements.get('cart-items-component')) {
  customElements.define('cart-items-component', CartItemsComponent);
}

if (document.documentElement.dataset.prevuraCartAcknowledgementsReady !== 'true') {
  document.addEventListener('invalid', handleCartAcknowledgementInvalid, true);
  document.addEventListener('change', handleCartAcknowledgementChange);
  document.addEventListener('toggle', handleCartParticipationToggle, true);
  document.addEventListener('click', handleCartDisclosureSummaryClick);
  document.documentElement.dataset.prevuraCartAcknowledgementsReady = 'true';
}

updateCartAcknowledgementUi();

